import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { ESF_STATIONLIST, MainContentPaddingTypes, NewButtonList, NewModal, NewSelect, SystemControlsInner, ActiveStationsList } from "../..";
import { useSectionOptions } from "../../_Components";
import { useEsfContext } from "../_EsfWalkThrough/EsfContext";
import { ButtonTypes, ListStyle_Types, MenuFloats } from "../../../Definitions";
import { selectStationByLocationId } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { selectLocationsBySection } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/locationSlice";
import { selectSectionsBySystem } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/sectionsSlice";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { selectLocationOptionsBySystem } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { isEqual, set } from "lodash";
import { useGateControl } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/gateControlSlice";
import { useTerminals } from "../../../Services";
import { selectTerminalByIds } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/LocationFeedingSlices/terminalSlice";
import { useStationStatusById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationStatusSlice";
import ActiveStationsGateStateList from "../SubComponents/activeStationsGateStateList";
import { fetchStationStatusByStationIdsThunk } from "../../../Store/Thunks/stationStatusByIds";

interface GateControlEsfWfLocation_Props {
  onClose: () => void;
  focus_view?: boolean;
}

export default function GateControlEsfWfLocationModal({ onClose, focus_view }: GateControlEsfWfLocation_Props) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { state: {system, selectedLocation, selectedStations, error, connection, domain}, dispatch: dispatchEsf } = useEsfContext();
  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;
  const [loading, setLoading] = useState<boolean>(false);
  
  const sections = useAppSelector((state) => selectSectionsBySystem(state, system.system_id ?? -1), isEqual);
  const locations = useAppSelector((state) => selectLocationsBySection(state, sections.map(sec => sec.section_id)), isEqual);
  const stations = useAppSelector((state) => selectStationByLocationId(state, selectedLocation?.location_id ?? -1), isEqual);

  const { gateControl, gateState } = useGateControl()
  
  useSectionOptions();
  const locationOptions = useAppSelector((state) => selectLocationOptionsBySystem(state, [system.system_id]), isEqual);
  
  useEffect(() => {
    if (!locations || locations.length === 0) return;
  
    const first = locations[0];
  
    if (!first?.stations) return;
  
    dispatchEsf({
      selectedLocation: first,
      selectedSection: sections.find(s => s.section_id === first.sectionId),
      selectedStations: stations.filter(s => first.stations.includes(s.station_id))
    });
  }, [dispatchEsf, sections]);

  function resetResultState() {
    // abortPreviousOperation();
    setLoading(false);
    dispatchEsf({ locationResults: [], selectedStations: [] })
  }

  const updateState = async(t:number) => {
    for (let i = 0; i < 3; i++) {
      dispatch(fetchStationStatusByStationIdsThunk(stations.map(s => s.station_id)));
      console.log("dut");
      setLoading(true);
  
      if (i < 3) {
        await new Promise((resolve) => setTimeout(resolve, t));
      }
    }
    setLoading(false);
  };

  if (!locations) return null;
  const currentIndex = locations.findIndex((l) => l.location_id === selectedLocation?.location_id);

  if (!selectedLocation) return;

  const allSelected = stations.every(stationId => selectedStations?.includes(stationId));

  return (
    <NewModal
      header={t("systemControls.general.systemControls", { defaultValue: "System Controls" })}
      subHeader={selectedLocation?.name}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary,
        headerContentHeight: focus_view ? "60px" : undefined,
        mainContentScroll: mobile ? true : false
      }}
      responsiveOverrides={{
        focus_view: focus_view,
        mobile: mobile
      }}
      predefinedButtons={{
        onClose: () => { dispatchEsf({selectedLocation: locations[0]});  resetResultState(); /* setStaticFeedAmount(0); */ onClose() },
        onNext: () => { dispatchEsf({selectedLocation: locations[(currentIndex + 1) % locations.length]}); resetResultState() },
        onPrev: () => { dispatchEsf({selectedLocation: locations[(currentIndex - 1 + locations.length) % locations.length]}); resetResultState() },
      }}
      headerContent={selectedLocation ? (
        <NewSelect
          options={locationOptions ?? []}
          key={selectedLocation.location_id}
          onChange={(i) => { /*resetResultState();*/ dispatchEsf({selectedLocation: locations.find(location => location.location_id === i?.id)})}}
          valueOverrides={{
            defaultValue: locationOptions?.find(option => option.id === selectedLocation.location_id)
          }}
          fieldOverrides={{
            fieldWidth: mobile ? "120px" : "160px",
            focus_view: focus_view,
            menuWidth: mobile ? "70vw" : undefined,
            menuFloat: MenuFloats.center
          }}
        />
      ) : (<></>)}
      mainContent={
        <SystemControlsInner
          activeLocationsListHeader={(t("systemControls.general.controls", { defaultValue: "Controls" }))}
          focus_view={focus_view}
          locationVisual={ selectedLocation &&
            <ESF_STATIONLIST
              key={selectedLocation.location_id}
              focus_view={focus_view}
              location={selectedLocation}
            />
          }
          activeLocationsList={
            <ActiveStationsGateStateList
              focus_view={focus_view}
            />
          }
          error={{
            errorText: error.errorText,
            elementId: error.Elementid
          }}
          selectLocation={{
            selectedLocations: selectedStations ?? [],
            isAllSelected: allSelected,
            handleSelectAll: () => {
              if (allSelected) { dispatchEsf({ selectedStations: selectedStations?.filter(stationId => !stations.includes(stationId)) });
              } else {
                const newSelection = Array.from(new Set([...selectedStations ?? [], ...stations]));
                dispatchEsf({ selectedStations: newSelection });
              }
            }
          }}
          selectContent={
            <NewButtonList
              disabled={[loading, loading, loading, loading]}
              contents={[
                t("systemControls.general.openGate", {defaultValue: "open gate"}),
                t("systemControls.general.closeGate", {defaultValue: "close gate"}),
                t("systemControls.general.blockGate", {defaultValue: "block gate"}),
                t("systemControls.general.unblockGate", {defaultValue: "unblock gate"})
              ]}
              buttonType={ButtonTypes.globalMenuButton}
              listOverrides={{
                listStyle: ListStyle_Types.group,
              }}
              startEnhancers={[
                glyphs.GateOpen({size: "40px"}),
                glyphs.Gate({size: "40px"}),
                glyphs.GateBlock({size: "40px"}),
                glyphs.GateBlock({size: "40px"})
              ]}
              onClick={(i) => {
                if (!selectedStations?.length) return;
              
                if (i === 0) { gateControl(selectedStations.map((station) => ({ stationId: station.station_id, secure: false, closeGate: false }))); updateState(1000)}
                if (i === 1) { gateControl( selectedStations.map((station) => ({ stationId: station.station_id, secure: false, closeGate: true }))); updateState(1000)}
                if (i === 2) { gateState(selectedStations.map((station) => ({ stationId: station.station_id, reload: false, state: 224 }))); updateState(1000)}
                if (i === 3) { gateState( selectedStations.map((station) => ({ stationId: station.station_id, reload: false, state: 16 }))); updateState(4000)}
              }}
              overrides={{
                baseStyles: { backgroundColor: colors.greySecondary, width: "135px", opacity: loading ? "0.5" : undefined },
                hoverStyles: { backgroundColor: colors.greyDarkSecondary },
              }}
            />
          }
        />
      }
    />
  )
}